import React, { useRef, useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import { Spinner } from "reactstrap";
import {
  setShippingData,
  fetchShippingCost,
  fetchMarkets,
} from "../../../redux/checkout/action";
import { searchCity, validateCapCity } from "../../../util/comuniValidation";
import { getDeliveryStoreId as getDeliveryStoreIdHelper } from "../../../helper/checkoutStore";

function DeliveryForm(props) {
  const {
    shippingData,
    shippingCost,
    shippingCostLoading,
    shippingCostError,
    shippingAvailable,
    markets,
    validationErrors,
    ecommerceConfig = {},
  } = props.checkout;

  const debounceRef = useRef(null);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [capCityError, setCapCityError] = useState(null);
  const [validatingCapCity, setValidatingCapCity] = useState(false);
  const suggestionsRef = useRef(null);

  const cartTotal = props.groceryList.list
    .filter((item) => !item.no_price)
    .reduce((sum, item) => sum + parseFloat(item.currentPrice || 0), 0);

  useEffect(() => {
    if (!markets || markets.length === 0) {
      props.fetchMarkets();
    }
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Close suggestions on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const getDeliveryStoreId = () => getDeliveryStoreIdHelper(ecommerceConfig, markets);

  const doCapCityValidation = useCallback(async (cap, citta) => {
    if (!cap || !/^\d{5}$/.test(cap) || !citta || citta.trim().length < 2) {
      setCapCityError(null);
      return false;
    }

    setValidatingCapCity(true);
    setCapCityError(null);

    try {
      const result = await validateCapCity(cap, citta);

      if (!result.cityFound) {
        setCapCityError("Città non trovata nel database");
        setValidatingCapCity(false);
        return false;
      }

      if (!result.valid) {
        const suggestion = result.validCaps.length > 0
          ? ` (CAP validi: ${result.validCaps.slice(0, 3).join(", ")}${result.validCaps.length > 3 ? "..." : ""})`
          : "";
        setCapCityError(`Il CAP ${cap} non corrisponde a ${citta}${suggestion}`);
        setValidatingCapCity(false);
        return false;
      }

      setCapCityError(null);
      setValidatingCapCity(false);

      // CAP-City OK → calcola spedizione
      const storeId = getDeliveryStoreId();
      if (storeId) {
        props.fetchShippingCost(storeId, cap, cartTotal);
      }
      return true;
    } catch {
      setValidatingCapCity(false);
      return false;
    }
  }, [markets, cartTotal, ecommerceConfig.storeIdMaster]);

  const handleCitySearch = async (value) => {
    props.setShippingData({ citta: value });
    setCapCityError(null);

    if (value.length >= 2) {
      const results = await searchCity(value);
      setCitySuggestions(results);
      setShowSuggestions(results.length > 0);
    } else {
      setCitySuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectCity = (comune) => {
    props.setShippingData({ citta: comune.nome });
    setShowSuggestions(false);
    setCitySuggestions([]);

    // If CAP is filled, auto-validate
    if (shippingData.cap && /^\d{5}$/.test(shippingData.cap)) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        doCapCityValidation(shippingData.cap, comune.nome);
      }, 200);
    }

    // If city has only one CAP, auto-fill it
    if (comune.cap && comune.cap.length === 1 && !shippingData.cap) {
      props.setShippingData({ cap: comune.cap[0] });
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        doCapCityValidation(comune.cap[0], comune.nome);
      }, 200);
    }
  };

  const handleCapChange = (e) => {
    const cap = e.target.value.replace(/\D/g, "").slice(0, 5);
    props.setShippingData({ cap });
    setCapCityError(null);

    if (/^\d{5}$/.test(cap) && shippingData.citta && shippingData.citta.trim().length >= 2) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        doCapCityValidation(cap, shippingData.citta);
      }, 400);
    }
  };

  const handleChange = (field) => (e) => {
    props.setShippingData({ [field]: e.target.value });
  };

  return (
    <div style={{ marginTop: 14 }}>
      <div className="co-form__group">
        <label className="co-form__label">Indirizzo</label>
        <input
          className={"co-form__input" + (validationErrors.indirizzo ? " co-form__input--error" : "")}
          type="text"
          value={shippingData.indirizzo}
          onChange={handleChange("indirizzo")}
          placeholder="Via, numero civico"
        />
        {validationErrors.indirizzo && (
          <span className="co-form__error">{validationErrors.indirizzo}</span>
        )}
      </div>

      <div className="co-form__row">
        <div className="co-form__group" style={{ flex: 2, position: "relative" }} ref={suggestionsRef}>
          <label className="co-form__label">Città</label>
          <input
            className={"co-form__input" + (validationErrors.citta || (capCityError && !capCityError.includes("CAP")) ? " co-form__input--error" : "")}
            type="text"
            value={shippingData.citta}
            onChange={(e) => handleCitySearch(e.target.value)}
            onFocus={() => citySuggestions.length > 0 && setShowSuggestions(true)}
            placeholder="Inizia a digitare..."
            autoComplete="off"
          />
          {showSuggestions && citySuggestions.length > 0 && (
            <div style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              backgroundColor: "#fff",
              border: "1px solid #e0e0e0",
              borderRadius: "0 0 10px 10px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              zIndex: 20,
              maxHeight: "180px",
              overflowY: "auto",
            }}>
              {citySuggestions.map((comune, idx) => (
                <div
                  key={idx}
                  onClick={() => handleSelectCity(comune)}
                  style={{
                    padding: "8px 14px",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    borderBottom: idx < citySuggestions.length - 1 ? "1px solid #f5f5f5" : "none",
                  }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = "#f5f5f5")}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = "#fff")}
                >
                  <strong>{comune.nome}</strong>
                  <span style={{ color: "#888", marginLeft: 6, fontSize: "0.82rem" }}>
                    ({comune.sigla})
                  </span>
                </div>
              ))}
            </div>
          )}
          {validationErrors.citta && (
            <span className="co-form__error">{validationErrors.citta}</span>
          )}
        </div>
        <div className="co-form__group" style={{ flex: 1 }}>
          <label className="co-form__label">CAP</label>
          <input
            className={"co-form__input" + (validationErrors.cap || capCityError ? " co-form__input--error" : "")}
            type="text"
            inputMode="numeric"
            value={shippingData.cap}
            onChange={handleCapChange}
            placeholder="00000"
            maxLength={5}
          />
          {validationErrors.cap && (
            <span className="co-form__error">{validationErrors.cap}</span>
          )}
        </div>
      </div>

      {/* CAP-City validation feedback */}
      {capCityError && (
        <div style={{ color: "#dc3545", fontSize: "0.85rem", fontWeight: 600, marginTop: -6, marginBottom: 8 }}>
          {capCityError}
        </div>
      )}

      {/* Shipping cost feedback */}
      <div style={{ marginTop: 4, minHeight: 24 }}>
        {validatingCapCity && (
          <span style={{ color: "#888", fontSize: "0.88rem" }}>
            <Spinner size="sm" style={{ marginRight: 6, width: 14, height: 14 }} />
            Verifica città e CAP...
          </span>
        )}
        {!validatingCapCity && !capCityError && shippingCostLoading && (
          <span style={{ color: "#888", fontSize: "0.88rem" }}>
            <Spinner size="sm" style={{ marginRight: 6, width: 14, height: 14 }} />
            Calcolo spedizione...
          </span>
        )}
        {!validatingCapCity && !capCityError && !shippingCostLoading && shippingCostError && (
          <span style={{ color: "#dc3545", fontSize: "0.88rem", fontWeight: 600 }}>
            {shippingCostError}
          </span>
        )}
        {!validatingCapCity && !capCityError && !shippingCostLoading && !shippingCostError && shippingAvailable === true && shippingCost !== null && (
          <span style={{ color: "#28a745", fontSize: "0.88rem", fontWeight: 600 }}>
            Spedizione: {shippingCost === 0 ? "Gratuita" : "€ " + parseFloat(shippingCost).toFixed(2)}
          </span>
        )}
        {!validatingCapCity && !capCityError && !shippingCostLoading && !shippingCostError && shippingAvailable === false && (
          <span style={{ color: "#dc3545", fontSize: "0.88rem", fontWeight: 600 }}>
            Spedizione non disponibile per questo CAP
          </span>
        )}
      </div>

      {/* Note */}
      <div className="co-form__group" style={{ marginTop: 8 }}>
        <label className="co-form__label">Note per la consegna (opzionale)</label>
        <textarea
          className="co-form__input"
          value={shippingData.note || ""}
          onChange={handleChange("note")}
          placeholder="Citofono, piano, indicazioni..."
          rows={2}
          style={{ resize: "vertical" }}
        />
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  checkout: state.checkout,
  groceryList: state.groceryList,
});

export default connect(mapStateToProps, {
  setShippingData,
  fetchShippingCost,
  fetchMarkets,
})(DeliveryForm);
